import metaModel from '~/shared/src/utilities/metaModel';
import {itemOnlyAttributesSettable} from '~/shared/src/ci/Item';
import {propertyAttributesSettable} from '~/shared/src/ci/Property';
import {referenceOnlyAttributesSettable} from '~/shared/src/ci/Reference';
import {referencePropertyAttributesSettable} from '~/shared/src/ci/ReferenceProperty';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import User from '~/shared/src/model/User';
import {BaseRequestParameters} from '~/shared/src/utilities/modules/common';
import {
    ColumnFilter,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';
import {
    DataDescriptor,
    DataStructure,
    getReferenceSetsForDataObject
} from '~/shared/src/utilities/dataStructure';

import {store} from '~/frontend/src/store';


/*** type definitions ************************************************************************************************/

export type LoadDataParameters = BaseRequestParameters & {
    searchString? : string;
    columns? : string[];
    filters? : ColumnFilter[];
    sorters? : ColumnSorter[];
    start? : number;
    limit? : number;
};
export const loadDataParameterDefaults = {
    columns: [],
    filters: [],
    sorters: [],
    start  : 0,
    limit  : 100
};


/*** function definitions ********************************************************************************************/

/**
 * Is column data editable
 *
 * @param {DataDescriptor} columnDataDescriptor
 * @param {DataStructure} dataStructure
 * @param {Schema} schema
 * @param {GroupPermissions} permissions
 * @param {{ [ parameter : string ] : any }} additionalCheckParameters
 *
 * @return {boolean}
 */
export const isColumnEditable = ( columnDataDescriptor : DataDescriptor, dataStructure : DataStructure, schema : Schema, permissions? : GroupPermissions, additionalCheckParameters? : { [ parameter : string ] : any } ) : boolean => {
    let retVal : boolean = false,
        user : User | false | undefined;

    // get user
///////////////////////////////////////////
    user = store.getState()?.session?.user;
//////////////
///////////////////////////////////////////
///////////////////////
//////////////

    if ( user && !columnDataDescriptor.aggregate && columnDataDescriptor.type !== 'expression' ) {

        // permission check
        if ( !permissions?.update?.length || user.isMemberOfGroup( permissions.update ) ) {
            retVal = true;
        }

        // check if attribute settable and user has permission therefore
        if ( retVal ) {
            const itemTypeName = String( columnDataDescriptor.dataObject.item.objectType ?? '' );

            let referenceSets : ReferenceSet[],
                setId : number | undefined;

            switch ( columnDataDescriptor.type ) {
                case 'item':
                    retVal = itemOnlyAttributesSettable.includes( columnDataDescriptor.attribute ) && user.hasPermissionOnItemType( 'update', itemTypeName, schema );

                    if ( retVal && columnDataDescriptor.attribute === 'key' ) {
                        const itemType = metaModel[ schema ].getItemType( itemTypeName );

                        if ( itemType?.isKeyAutogenerated ) {
                            retVal = false;
                        }
                    }
                    break;

                case 'property':
                    retVal = propertyAttributesSettable.includes( columnDataDescriptor.attribute );

                    if ( retVal ) {
                        setId = metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( itemTypeName, columnDataDescriptor.propertyTypeName )?.id;

                        retVal = !!setId && user.hasPermissionOnPropertySet( 'update', setId, schema );
                    }
                    break;

                case 'reference':
                    retVal = referenceOnlyAttributesSettable.includes( columnDataDescriptor.attribute );

                    if ( retVal ) {
                        if ( additionalCheckParameters?.referenceSetId ) {
                            retVal = user.hasPermissionOnReferenceSet( 'update', additionalCheckParameters?.referenceSetId, schema );
                        }
                        // check if at least one referenceSet is updatable
                        else {
                            retVal = false;
                            referenceSets = getReferenceSetsForDataObject( columnDataDescriptor.dataObject, dataStructure, schema );

                            if ( referenceSets.length ) {
                                for ( const referenceSet of referenceSets ) {
                                    retVal = retVal || user.hasPermissionOnReferenceSet( 'update', referenceSet.id, schema );

                                    if ( retVal ) {
                                        break;
                                    }
                                }
                            }
                        }
                    }
                    break;

                case 'referenceProperty':
                    retVal = referencePropertyAttributesSettable.includes( columnDataDescriptor.attribute );

                    if ( retVal ) {
                        setId = getReferenceSetsForDataObject( columnDataDescriptor.dataObject, dataStructure, schema )[ 0 ]?.id;
                        setId = setId
                            ? metaModel[ schema ].getReferencePropertySetForReferenceSetAndPropertyType( setId, columnDataDescriptor.propertyTypeName )?.id
                            : undefined;

                        retVal = !!setId && user.hasPermissionOnReferencePropertySet( 'update', setId, schema );
                    }
            }
        }
    }

    return retVal;
};
